import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';
import { calculateTotalImpact, validateSufficientBalance } from '@/lib/balanceCalculator';
import { InsufficientBalanceError, AccountNotFoundError, AtomicOperationError } from '@/lib/errors/transactionErrors';
import { convertTransactionAmounts } from '@/lib/currencyConversion/currencyConversion';
import { fetchRatesWithRetry } from '@/integrations/api/currency';

import type { TransactionWithRelations as Transaction } from '@/integrations/supabase/transaction.models';

export type { Transaction };

export function useTransactions(filters?: {
  type?: string;
  accountId?: string;
  categoryId?: string;
  dateFrom?: string;
  dateTo?: string;
  searchQuery?: string;
}) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['transactions', user?.id, filters],
    queryFn: async () => {
      let query = supabase
        .from('transactions')
        .select('*, accounts(name), categories(name, icon), sub_categories(name, icon)')
        .order('transaction_date', { ascending: false });

      if (filters?.type) query = query.eq('type', filters.type);
      if (filters?.accountId) query = query.eq('account_id', filters.accountId);
      if (filters?.categoryId) query = query.eq('category_id', filters.categoryId);
      if (filters?.dateFrom) query = query.gte('transaction_date', filters.dateFrom);
      if (filters?.dateTo) query = query.lte('transaction_date', filters.dateTo);

      if (filters?.searchQuery) {
        const queryTerm = `%${filters.searchQuery}%`;
        const [
          { data: accData },
          { data: catData },
          { data: subCatData }
        ] = await Promise.all([
          supabase.from('accounts').select('id').ilike('name', queryTerm),
          supabase.from('categories').select('id').ilike('name', queryTerm),
          supabase.from('sub_categories').select('id').ilike('name', queryTerm)
        ]);

        const orClauses = [`description.ilike.${queryTerm}`];
        if (accData && accData.length > 0) {
          orClauses.push(`account_id.in.(${accData.map(a => a.id).join(',')})`);
        }
        if (catData && catData.length > 0) {
          orClauses.push(`category_id.in.(${catData.map(c => c.id).join(',')})`);
        }
        if (subCatData && subCatData.length > 0) {
          orClauses.push(`sub_category_id.in.(${subCatData.map(s => s.id).join(',')})`);
        }
        query = query.or(orClauses.join(','));
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Transaction[];
    },
    enabled: !!user,
  });
}

export function useTransactionsPaginated(
  filters?: {
    type?: string;
    accountId?: string;
    categoryId?: string;
    dateFrom?: string;
    dateTo?: string;
    searchQuery?: string;
  },
  pageSize: number = 50
) {
  const { user } = useAuth();
  
  return useInfiniteQuery({
    queryKey: ['transactions-paginated', user?.id, filters, pageSize],
    queryFn: async ({ pageParam = 0 }) => {
      let query = supabase
        .from('transactions')
        .select('*, accounts(name), categories(name, icon), sub_categories(name, icon)', { count: 'exact' })
        .order('transaction_date', { ascending: false })
        .range(pageParam, pageParam + pageSize - 1);

      if (filters?.type) query = query.eq('type', filters.type);
      if (filters?.accountId) query = query.eq('account_id', filters.accountId);
      if (filters?.categoryId) query = query.eq('category_id', filters.categoryId);
      if (filters?.dateFrom) query = query.gte('transaction_date', filters.dateFrom);
      if (filters?.dateTo) query = query.lte('transaction_date', filters.dateTo);

      if (filters?.searchQuery) {
        const queryTerm = `%${filters.searchQuery}%`;
        const [
          { data: accData },
          { data: catData },
          { data: subCatData }
        ] = await Promise.all([
          supabase.from('accounts').select('id').ilike('name', queryTerm),
          supabase.from('categories').select('id').ilike('name', queryTerm),
          supabase.from('sub_categories').select('id').ilike('name', queryTerm)
        ]);

        const orClauses = [`description.ilike.${queryTerm}`];
        if (accData && accData.length > 0) {
          orClauses.push(`account_id.in.(${accData.map(a => a.id).join(',')})`);
        }
        if (catData && catData.length > 0) {
          orClauses.push(`category_id.in.(${catData.map(c => c.id).join(',')})`);
        }
        if (subCatData && subCatData.length > 0) {
          orClauses.push(`sub_category_id.in.(${subCatData.map(s => s.id).join(',')})`);
        }
        query = query.or(orClauses.join(','));
      }

      const { data, error, count } = await query;
      if (error) throw error;
      
      return {
        transactions: data as Transaction[],
        nextCursor: pageParam + pageSize,
        hasMore: count ? pageParam + pageSize < count : false,
        totalCount: count || 0,
      };
    },
    getNextPageParam: (lastPage) => {
      return lastPage.hasMore ? lastPage.nextCursor : undefined;
    },
    initialPageParam: 0,
    enabled: !!user,
  });
}

export function useCreateTransaction() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (tx: Omit<TablesInsert<'transactions'>, 'user_id'>) => {
      if (!user) throw new Error('User not authenticated');

      // Fetch current account balance and currency
      const { data: account, error: accountError } = await supabase
        .from('accounts')
        .select('balance, currency')
        .eq('id', tx.account_id)
        .eq('user_id', user.id)
        .single();

      if (accountError || !account) {
        throw new AccountNotFoundError(tx.account_id);
      }

      // Convert amount and fee to the account's native currency
      const nativeCurrency = account.currency;
      const { convertedAmount, convertedFee } = await convertTransactionAmounts(
        {
          amount: tx.amount,
          fee: tx.fee ?? 0,
          fromCurrency: tx.currency,
          toCurrency: nativeCurrency,
        },
        fetchRatesWithRetry
      );

      // Calculate total impact using converted values
      const { totalImpact } = calculateTotalImpact(
        convertedAmount,
        convertedFee,
        tx.type as 'income' | 'expense'
      );

      // Validate sufficient balance for expense transactions
      const validation = validateSufficientBalance(
        account.balance,
        totalImpact,
        tx.type as 'income' | 'expense',
        nativeCurrency,
        'create'
      );

      if (!validation.isValid && validation.error) {
        throw new InsufficientBalanceError(
          validation.error.currentBalance,
          validation.error.requiredAmount,
          validation.error.shortfall,
          validation.error.currency
        );
      }

      // Call RPC function to create transaction with balance update
      try {
        const { data, error } = await (supabase.rpc as any)('create_transaction_with_balance', {
          p_user_id: user.id,
          p_account_id: tx.account_id,
          p_amount: convertedAmount,
          p_fee: convertedFee,
          p_type: tx.type,
          p_transaction_date: tx.transaction_date,
          p_currency: nativeCurrency,
          p_category_id: tx.category_id ?? null,
          p_sub_category_id: (tx as any).sub_category_id ?? null,
          p_description: tx.description ?? null,
          p_notes: tx.notes ?? null
        });

        if (error) throw error;
        return data;
      } catch (error) {
        throw new AtomicOperationError('create_transaction', error as Error);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

export function useUpdateTransaction() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ id, ...updates }: TablesUpdate<'transactions'> & { id: string }) => {
      if (!user) throw new Error('User not authenticated');

      // Fetch original transaction data
      const { data: originalTransaction, error: fetchError } = await supabase
        .from('transactions')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .single();

      if (fetchError || !originalTransaction) {
        throw new Error(`Transaction not found: ${id}`);
      }

      // Determine final values (use updates or fall back to original)
      const finalAmount = updates.amount ?? originalTransaction.amount;
      const finalFee = updates.fee ?? originalTransaction.fee;
      const finalType = (updates.type ?? originalTransaction.type) as 'income' | 'expense';
      const finalAccountId = updates.account_id ?? originalTransaction.account_id;
      const isAccountChanged = updates.account_id && updates.account_id !== originalTransaction.account_id;

      // For same-account updates, validate balance delta
      if (!isAccountChanged) {
        // Fetch current account balance
        const { data: account, error: accountError } = await supabase
          .from('accounts')
          .select('balance, currency')
          .eq('id', finalAccountId)
          .eq('user_id', user.id)
          .single();

        if (accountError || !account) {
          throw new AccountNotFoundError(finalAccountId);
        }

        // Calculate balance delta
        const oldImpact = calculateTotalImpact(
          originalTransaction.amount,
          originalTransaction.fee,
          originalTransaction.type as 'income' | 'expense'
        );
        const newImpact = calculateTotalImpact(
          finalAmount,
          finalFee,
          finalType
        );

        // For expense transactions, validate the delta won't cause negative balance
        if (finalType === 'expense') {
          const delta = newImpact.totalImpact - oldImpact.totalImpact;
          
          // Only validate if the expense is increasing
          if (delta > 0) {
            const validation = validateSufficientBalance(
              account.balance,
              delta,
              'expense',
              account.currency,
              'update'
            );

            if (!validation.isValid && validation.error) {
              throw new InsufficientBalanceError(
                validation.error.currentBalance,
                validation.error.requiredAmount,
                validation.error.shortfall,
                validation.error.currency
              );
            }
          }
        }
      } else {
        // For cross-account updates, validate the new account has sufficient balance
        if (finalType === 'expense') {
          const { data: newAccount, error: newAccountError } = await supabase
            .from('accounts')
            .select('balance, currency')
            .eq('id', finalAccountId)
            .eq('user_id', user.id)
            .single();

          if (newAccountError || !newAccount) {
            throw new AccountNotFoundError(finalAccountId);
          }

          const { totalImpact } = calculateTotalImpact(finalAmount, finalFee, finalType);
          const validation = validateSufficientBalance(
            newAccount.balance,
            totalImpact,
            finalType,
            newAccount.currency,
            'update'
          );

          if (!validation.isValid && validation.error) {
            throw new InsufficientBalanceError(
              validation.error.currentBalance,
              validation.error.requiredAmount,
              validation.error.shortfall,
              validation.error.currency
            );
          }
        }
      }

      // Call RPC function to update transaction with balance adjustment
      try {
        const { data, error } = await (supabase.rpc as any)('update_transaction_with_balance', {
          p_transaction_id: id,
          p_user_id: user.id,
          p_amount: updates.amount ?? null,
          p_fee: updates.fee ?? null,
          p_account_id: updates.account_id ?? null,
          p_type: updates.type ?? null,
          p_transaction_date: updates.transaction_date ?? null,
          p_currency: updates.currency ?? null,
          p_category_id: updates.category_id ?? null,
          ...subCategoryUpdateArgs(updates),
          p_description: updates.description ?? null,
          p_notes: updates.notes ?? null
        });

        if (error) throw error;
        return data;
      } catch (error) {
        throw new AtomicOperationError('update_transaction', error as Error);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

/**
 * Maps an edit's sub-category onto update_transaction_with_balance: a value sets it, null clears it
 * explicitly, and leaving it out of the edit keeps the current one.
 */
export function subCategoryUpdateArgs(updates: { sub_category_id?: string | null }): {
  p_sub_category_id?: string;
  p_clear_sub_category?: boolean;
} {
  if (!('sub_category_id' in updates) || updates.sub_category_id === undefined) return {};
  if (updates.sub_category_id === null) return { p_clear_sub_category: true };
  return { p_sub_category_id: updates.sub_category_id };
}

export function useDeleteTransaction() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (id: string) => {
      if (!user) throw new Error('User not authenticated');

      // Call RPC function to delete transaction with balance restoration
      try {
        const { error } = await (supabase.rpc as any)('delete_transaction_with_balance', {
          p_transaction_id: id,
          p_user_id: user.id
        });

        if (error) throw error;
      } catch (error) {
        throw new AtomicOperationError('delete_transaction', error as Error);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}
